/**
 * Разложить деньги.
 *
 * Экран отвечает на вопрос «что я могу сейчас» — и делает это версией, а не
 * пересчётом на каждый чих. Отсюда три правила, каждое из которых однажды
 * стоило бы человеку доверия:
 *
 * · **Сначала показать, потом применить.** Человек видит строки и суммы до
 *   нажатия; деньги не двигаются, пока он не согласился.
 * · **Приход доливает, а не перезапускает.** Новые деньги попадают в «не
 *   распределено», а прежние корзины остаются как были: покупка продуктов
 *   не должна уменьшать «Свободные траты», и приход не должен молча перекраивать
 *   всё, о чём человек уже договорился.
 * · **Нехватка — не повод раздать меньше молча.** Если защищённого слоя не
 *   хватает, цели и «Свободные траты» получают ноль, а человек видит, чего именно
 *   недостаёт, и подтверждает, что оставить необеспеченным.
 */
import { useState } from 'react';
import {
  ZERO, DEFAULT_MODE, MODE_TITLE, activeAllocation, add, clampPos,
  formatMoney, resolveHorizon, shortfallLines, sub, unsettledInstances,
  type Money, type ShortfallDecision,
} from '@skoka/core';
import { AmountInput } from './amount.js';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { flash } from './flash.js';
import {
  acceptAllocation, allocationProposal, allocationViewOf, formatAmountInput, horizonInput, parseAmountInput, planInput,
  type AppState,
} from './store.js';
import { dayMonth } from './text.js';

export function AllocateSheet({ state, onClose, onDone, onMode }: {
  state: AppState;
  onClose: () => void;
  onDone: () => void;
  /** Открыть выбор режима: он делит третий слой этого же предложения. */
  onMode?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [proposal, setProposal] = useState(() => allocationProposal(state));
  /*
    Решение при нехватке.

    `null` — человек его ещё не принимал, и раскладка с нехваткой не
    применяется: выбирать, какой обязательный платёж останется без денег,
    приложение не вправе. Рекомендацию оно показать может, действие —
    только человек.
  */
  const [decision, setDecision] = useState<ShortfallDecision | null>(null);
  const [conflict, setConflict] = useState(false);
  const [funds, setFunds] = useState<Record<string, string>>({});

  const current = activeAllocation(state.allocations);
  const overdueFound = unsettledInstances({
    obligations: state.obligations,
    reserves: state.reserves,
    today: state.today,
    ...(state.trackingStartedAt ? { trackingStartedAt: state.trackingStartedAt } : {}),
  }).some((i) => i.scheduledDate < state.today && i.unpaid > ZERO);
  const before = current ? allocationViewOf(state, current) : null;

  const { lines: plan } = proposal;
  /*
    Срок называется только когда он известен.

    «Не хватает 18 000 ₽ до 5 октября» и «не хватает 18 000 ₽» — разные
    сообщения: первое даёт человеку точку, к которой надо успеть, второе
    честно молчит там, где даты нет.
  */
  const horizon = resolveHorizon(horizonInput(state));
  const horizonWord = horizon.basis === 'confirmed' && horizon.date
    ? ` до ${dayMonth(horizon.date, state.today)}`
    : '';
  const toGoals = add(...plan.goals.map((g) => g.amount));
  // Платежи по картам — в той же строке: для человека это такие же платежи.
  const payLines = [...plan.obligations, ...(plan.cards ?? [])];
  const toObligations = add(...payLines.map((o) => o.amount));
  const row = (label: string, value: Money, note?: string) => (
    <div className="row" key={label}>
      <div className="rl">{label}{note && <span className="rn">{note}</span>}</div>
      <div className="rv num">{formatMoney(value)}</div>
    </div>
  );

  const lines = conflict ? shortfallLines(planInput(state)) : [];
  const available = clampPos(sub(proposal.liquid, proposal.reserved));
  const chosen = (id: string, fallback: Money): Money => {
    const raw = funds[id];
    if (raw === undefined || raw.trim() === '') return fallback;
    return parseAmountInput(raw);
  };
  const totalChosen = add(...lines.map((l) => chosen(l.id, l.proposed)));
  const overspent = totalChosen > available;

  if (conflict) {
    /*
      Экран конфликта.

      Здесь приложение перестаёт предлагать и начинает спрашивать. Оно вправе
      показать рекомендацию, но не вправе её применить: выбор между
      просрочкой по ипотеке и пустым холодильником — не арифметика.
    */
    return (
      <SheetWrap title={`Не хватает ${formatMoney(proposal.shortfall)}`} onClose={() => setConflict(false)}
        tip={<>Цели и свободные траты в этот раз не получают ничего: раздавать то, чего не
          хватает на платежи и еду, — самый дорогой вид оптимизма.</>}>
        <div className="note" style={{ marginTop: 0 }}>
          Защитить всё одновременно не получится. Выбери, как распределить
          {' '}{formatMoney(available)}: что обеспечить полностью, что частично,
          а что оставить.
        </div>

        {lines.map((l) => (
          <div className="li fund" key={l.id}>
            <div className="nm">
              <b>{l.label}</b>
              <span>
                нужно {formatMoney(l.required)}
                {l.dueDate ? ` · к ${dayMonth(l.dueDate, state.today)}` : ''}
                {l.secured > ZERO ? ` · обеспечено ${formatMoney(l.secured)}` : ''}
              </span>
              <span className="rn">{l.consequence}</span>
            </div>
            <div className="fundside">
              <AmountInput value={funds[l.id] ?? formatAmountInput(String(l.proposed / 100))}
                className="fundamt" label={`Выделить: ${l.label}`}
                onChange={(v) => setFunds((f) => ({ ...f, [l.id]: v }))} />
              <div className="am num">
                останется {formatMoney(clampPos(sub(l.required, add(l.secured, chosen(l.id, l.proposed)))))}
              </div>
            </div>
          </div>
        ))}

        <div className="row"><div className="rl">Всего распределено</div>
          <div className="rv num">{formatMoney(totalChosen)} из {formatMoney(available)}</div></div>
        {overspent && (
          <div className="note warn">
            Больше, чем есть. Уменьши какую-нибудь строку — денег от этого не
            прибавится, а обещание станет ложным.
          </div>
        )}

        <button className="btn pri" disabled={overspent}
          onClick={() => {
            const left = add(...lines.map(
              (l) => clampPos(sub(l.required, add(l.secured, chosen(l.id, l.proposed)))),
            ));
            setDecision({
              decidedAt: new Date().toISOString(),
              lines: lines.map((l) => ({ id: l.id, fund: chosen(l.id, l.proposed) })),
              left,
            });
            setConflict(false);
          }}>
          Сохранить решение
        </button>
        <button className="btn" onClick={() => setConflict(false)}>Назад</button>
      </SheetWrap>
    );
  }

  return (
    <SheetWrap title="Разложить деньги" onClose={onClose}>
      {/*
        Из чего складывается сумма, которую раскладываю заново. Раньше здесь
        стояло только «не распределено» (1 725,98), а раскладывалось 4 835,25:
        остаток корзин, перерасход и деньги мимо записей не назывались.
      */}
      {before && (
        <>
          <div className="pick"><div className="k">Из чего раскладываю</div></div>
          {row('Осталось в корзинах', add(clampPos(before.needLeft), clampPos(before.wantLeft)))}
          {before.overspent > ZERO && row('Перерасход корзин', sub(ZERO, before.overspent))}
          {before.carry > ZERO && row('Отложено до прихода', before.carry)}
          {row('Не распределено', before.unallocated)}
          {before.owed > ZERO && row('Покупки по кредитке, ещё не погашены', before.owed)}
          {before.unexplained !== ZERO && row(
            before.unexplained > ZERO ? 'На счетах больше, чем в корзинах' : 'На счетах меньше, чем в корзинах',
            before.unexplained,
            'раскладываю по тому, что на счетах сейчас',
          )}
          <div className="row"><div className="rl"><b>Раскладываю заново</b></div>
            <div className="rv num"><b>{formatMoney(available)}</b></div></div>
        </>
      )}

      {/*
        Планируем только до ближайшего прихода — и говорим об этом.

        Что будет после него, приложение не знает: следующей даты нет.
        Молчание здесь читалось бы как «дальше всё в порядке».
      */}
      <div className="pick"><div className="k">Куда предлагаю разложить
        {plan.carry === ZERO && horizon.basis === 'confirmed' && (
          <Tip>Считаю до ближайшего прихода {horizon.date ? dayMonth(horizon.date, state.today) : ''}. Что будет после него,
            посчитаем, когда деньги придут: сейчас следующей даты нет.</Tip>
        )}</div></div>
      {payLines.length > 0 && row(
        'Платежи', toObligations,
        payLines.map((o) => o.label).join(', '),
      )}
      {row('Повседневные нужды', plan.need, 'до ближайшего прихода')}
      {plan.carry > ZERO && row(
        'Перенос', plan.carry, 'ближайшего прихода не хватит на следующий отрезок',
      )}
      {plan.goals.length > 0 && row(
        'Цели', toGoals, plan.goals.map((g) => g.name).join(', '),
      )}
      {row('Свободные траты', plan.want)}
      {/* Режим делит только этот слой — и менять его человек должен здесь же,
          не уходя в настройки и не теряя предложение. */}
      {(plan.want > ZERO || plan.goals.length > 0) && onMode && (
        <button className="btn" onClick={onMode}>
          Как распределять остаток: {MODE_TITLE[state.planMode ?? DEFAULT_MODE].toLowerCase()}
        </button>
      )}
      {plan.unallocated > ZERO && row('Останется нераспределённым', plan.unallocated)}

      {proposal.shortfall > ZERO && (
        <>
          <div className="note warn">
            Не хватает {formatMoney(proposal.shortfall)}
            {horizonWord}. Защитить всё одновременно не получится — выбери,
            как распределить то, что есть.
          </div>
          {proposal.atRisk.length > 0 && (
            <div className="note warn">
              Взносы под угрозой: {proposal.atRisk.map((g) => g.name).join(', ')}.
              Сами цели остаются — меняется только этот период.
            </div>
          )}
          {!decision && (
            <button className="btn pri" onClick={() => setConflict(true)}>
              Выбрать, что обеспечить
            </button>
          )}
          {decision && (
            <div className="okline">
              Решение принято: необеспеченным остаётся {formatMoney(decision.left)}
            </div>
          )}
        </>
      )}

      {proposal.unknowns.map((u) => (
        <div className="note" key={u}>{u}</div>
      ))}

      {stale && (
        <div className="note warn">
          Пока ты читал, данные изменились — предложение пересчитано. Посмотри
          новые числа и подтверди их.
        </div>
      )}
      {err && <div className="note warn">{err}</div>}

      <button className="btn pri" disabled={busy || (proposal.shortfall > ZERO && !decision)}
        onClick={async () => {
          setBusy(true); setErr(null);
          try {
            const result = await acceptAllocation({
              state,
              proposal,
              // Основание — то, из-за чего пересчитываем: по нему видно
              // историю решений, а не только их результат.
              basis: overdueFound ? 'overdue_detected' : current ? 'income' : 'onboarding',
              ...(decision ? { shortfall: decision } : {}),
            });
            if (result.needsChoice) {
              setConflict(true);
              setBusy(false);
              return;
            }
            if (result.stale) {
              // Данные изменились между показом и нажатием: применять старое
              // предложение нельзя — закрепились бы суммы, которых уже нет.
              // Новый расчёт приходит вместе с отказом: пересчитывать его из
              // состояния экрана бессмысленно, оно тоже устарело.
              if (result.proposal) setProposal(result.proposal);
              setStale(true);
              setBusy(false);
              return;
            }
            flash('Раскладка принята');
            onDone();
          } catch (e) {
            setErr(e instanceof Error ? e.message : 'Не получилось');
            setBusy(false);
          }
        }}>
        {busy ? 'Применяю…' : 'Принять раскладку'}
      </button>
      <button className="btn" onClick={onClose}>Не сейчас</button>
    </SheetWrap>
  );
}
